import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { BASE_URL } from "../../base";
import { peekAppInsights } from "../../appInsights";
import { FeedbackResult, sendFeedbackReport } from "../../api/feedbackApi";
import { formatBytes } from "./helpers";
import { clearFailedRequest, FailedRequestRecord } from "./lastFailedRequest";
import { useProfile } from "./ProfileProvider";
import {
  apiHostOf,
  deriveEnvironment,
  describeBrowser,
  formatUtcTimestamp,
  nowUtcIso,
  ReportContext,
} from "./reportContext";
import { capturePath } from "./reportPath";
import { ReportProblemSource } from "./reportProblemBus";
import tailwindClassnames from "./TailwindClassnames";
import { popTopLayer, pushTopLayer } from "./topLayer";

/**
 * The Report a problem dialog (AKL-798).
 *
 * One dialog for all three entry points (the account menu, the unexpected-error page, and the
 * timeout/network toasts). It collects what the user typed, an optional screenshot, and the
 * context it captured when it opened, and posts one multipart `POST /feedback`.
 *
 * Mounted once, globally, by `ReportProblemHost` from `App.tsx` — never from `Header`, which
 * short-circuits to a logo-only skeleton while the profile loads.
 */

/** The largest screenshot accepted. The refusal message interpolates `formatBytes` of this. */
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

/**
 * The character limit on each text field, and **the same number the API bounds them at**.
 *
 * It is enforced in the browser with `maxLength` rather than left to the server: the API
 * answers an over-long field with a 422, which this dialog can only render as the generic
 * `Could not send your report. Please try again.` — a sentence that invites retrying
 * something that can never succeed. With `maxLength` the browser truncates the paste instead,
 * so the state is never reachable. If the API's bound changes, change this with it; the two
 * numbers only mean anything while they agree.
 */
const MAX_REPORT_FIELD_LENGTH = 10000;

/**
 * Elements the Tab-trap considers focusable.
 *
 * Duplicated from `ReviewPageFilter.tsx`, where it is a module-private `const` with no
 * `export` — an import would not resolve, and exporting it would edit a shared review
 * component this ticket is required to leave alone. One duplicated selector string is the
 * smaller change.
 *
 * The `tabIndex >= 0` filter applied alongside it matters: the panel itself and the hidden
 * file input both carry `tabIndex={-1}`, and without the filter the hidden input would join
 * the Tab cycle as an unfocusable member and strand the trap.
 */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * `detailsPageTextArea` hard-codes `h-48`, which is far too tall for a three-row and a
 * two-row field. Appending another `h-*` class does not reliably win — both utilities land in
 * the stylesheet and source order decides — so the token is filtered out of the composed
 * string instead. The shared constant is deliberately not edited: every existing consumer
 * relies on its fixed height.
 */
const TEXTAREA_CLASS = tailwindClassnames.detailsPageTextArea
  .split(" ")
  .filter((token) => token !== "h-48")
  .join(" ");

const DROP_ZONE_BASE =
  "flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue disabled:cursor-not-allowed disabled:opacity-45";
const DROP_ZONE_IDLE =
  "border-off-white-dark bg-off-white-dark/30 text-off-black-light dark:border-storm-blue-border dark:bg-storm-blue-light/10 dark:text-off-white-dark";
const DROP_ZONE_ACTIVE =
  "border-sky-blue-dark bg-sky-blue/20 text-storm-blue dark:border-sky-blue dark:bg-sky-blue/15 dark:text-primary-off-white";

/** The narrow shape of the App Insights session manager this dialog reads. */
type SessionManagerLike = {
  getSessionId?: () => string;
  automaticSession?: { id?: string };
};
type AppInsightsLike = { context?: { sessionManager?: SessionManagerLike } };

/**
 * The App Insights session id, best effort — read from an instance that already exists, and
 * **never constructed from here**.
 *
 * This dialog used to call `getAppInsights(emailRef.current)`, and on the entry points where
 * the profile has not resolved that argument is `""`: `getAppInsights` would then build and
 * cache the client while skipping `setAuthenticatedUserContext`, so every later page view and
 * exception in that browser session was recorded with no user id — the dialog silently
 * degrading telemetry for the whole session. `peekAppInsights` returns whatever
 * `ProfileProvider` already created and never creates one, which removes the hazard rather
 * than guarding it, and also keeps opening the dialog from firing the connection-string
 * request (which on a dead API would be recorded as the "last failed request" — by the
 * feature whose job is to report the real one).
 *
 * There is no top-level session-id getter: in `@microsoft/applicationinsights-web` 3.x
 * `getSessionId()` lives on `ISessionManager`, reached as `appInsights.context.sessionManager`,
 * with `automaticSession.id` as the alternative. `src/appInsights.js` is untyped JavaScript so
 * the whole chain types as `any`; it is narrowed through the two interfaces above rather than
 * an `as any` cast, and every hop is guarded.
 *
 * An empty result is normal, never a failure: App Insights is deliberately not initialised on
 * `/unexpectedError` or `/login`, which is one of this dialog's three entry points.
 * @returns the session id, or "" when there is not one to read
 */
function readAppInsightsSessionId(): string {
  try {
    const instance = peekAppInsights() as AppInsightsLike | null | undefined;
    const sessionManager = instance?.context?.sessionManager;
    if (!sessionManager) return "";
    const fromGetter =
      typeof sessionManager.getSessionId === "function"
        ? sessionManager.getSessionId()
        : "";
    return fromGetter || sessionManager.automaticSession?.id || "";
  } catch {
    return "";
  }
}

export interface ReportProblemModalProps {
  isOpen: boolean;
  /** Which entry point opened it; sent as the payload's `source`. */
  source: ReportProblemSource;
  /** The last failed request the app saw, read from session storage by the host. */
  failedRequest: FailedRequestRecord | null;
  /**
   * What had focus when the dialog was asked for; focus returns here on close. Captured by
   * the bus, not by this component — see `openerRef` below.
   */
  opener: HTMLElement | null;
  onClose: () => void;
  /** Raised after a successful send, so the host can toast. */
  onSent: () => void;
}

const ReportProblemModal: React.FC<ReportProblemModalProps> = ({
  isOpen,
  source,
  failedRequest,
  opener,
  onClose,
  onSent,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { profile } = useProfile();

  const [whatHappened, setWhatHappened] = useState("");
  const [expected, setExpected] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [context, setContext] = useState<ReportContext | null>(null);
  const [aiSessionId, setAiSessionId] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const whatHappenedRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /**
   * What to hand focus back to on close.
   *
   * It comes from the `opener` prop, which the bus read at the moment something asked for the
   * dialog. Reading `document.activeElement` here instead — as this dialog originally did, in
   * the `[isOpen]` effect — captures `<body>`: that effect runs after the commit in which the
   * account-menu dropdown was unmounted, so the trigger is already gone from the document and
   * the restore is a no-op, leaving a keyboard user's next Tab at the top of the page.
   *
   * Mirrored into a ref the way `pathRef`/`emailRef` are, so the open-transition effects can
   * read it without listing it as a dependency (which would re-run the capture effect and
   * wipe the form) and without silencing exhaustive-deps.
   */
  const openerRef = useRef<HTMLElement | null>(opener);
  useEffect(() => {
    openerRef.current = opener;
  }, [opener]);
  // A drag crossing a child element fires dragleave on the way; counting enter/leave pairs
  // keeps the hover styling from flickering off mid-drag.
  const dragDepth = useRef(0);

  // The capture effect below depends on `isOpen` alone — the context must describe where the
  // user was when the dialog opened, and re-running it on every route or profile change would
  // rewrite it underneath them. The two values it needs are therefore mirrored into refs
  // (the restructure CLAUDE.md asks for, rather than silencing exhaustive-deps).
  const pathRef = useRef(location.pathname);
  const emailRef = useRef(profile?.email ?? "");
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    emailRef.current = profile?.email ?? "";
  }, [profile]);

  /**
   * The open transition: capture the context once, clear anything left from a previous
   * report, and remember what to hand focus back to.
   *
   * The panel must tolerate `profile === null` — `ProfileProvider` starts with a null profile
   * and fetches `/users/me` on `/unexpectedError` too, so on the error-page entry point the
   * email is frequently in flight or failed. The User row falls back to the existing
   * translated `Unknown` key and no email is sent; the server is the authority on identity.
   *
   * ProfileProvider also fetches `/users/me` on `/unexpectedError`; if that fetch fails, the
   * interceptor now rejects without reloading this page, and the provider shows its existing
   * profile-error toast.
   */
  useEffect(() => {
    if (!isOpen) return;
    const captured = nowUtcIso();
    setContext({
      page: capturePath(pathRef.current),
      email: emailRef.current,
      browser: describeBrowser(),
      userAgent:
        typeof navigator === "undefined" ? "" : (navigator.userAgent ?? ""),
      environment: deriveEnvironment(BASE_URL),
      apiHost: apiHostOf(BASE_URL),
      clientTime: captured,
      clientTimeDisplay: formatUtcTimestamp(captured),
    });
    setWhatHappened("");
    setExpected("");
    setScreenshot(null);
    setPreviewUrl(null);
    setAttachError(null);
    setSendError(null);
    setSending(false);
    setIsDragActive(false);
    dragDepth.current = 0;
    setAiSessionId(readAppInsightsSessionId());
  }, [isOpen]);

  /**
   * Claim the top layer while open, so the review surface's keyboard handlers stand aside
   * (AKL-798).
   *
   * Four `window` listeners underneath this dialog would otherwise act on keys meant for it:
   * `PointToSpeciesModal` and `ObservationEditor` each cancel themselves on a capture-phase
   * Escape — which React's delegated handler on this panel cannot pre-empt, so one Escape
   * closed this dialog *and* threw away the species and comment staged underneath;
   * `FullScreenImageReview.handleSurfaceKey` runs its single-key shortcuts on any focus that
   * is not an input, so `s` committed the review behind a dialog the user cannot see past; and
   * `ManageReviewPage.handleKeyDown` does the same on the grid, where `s` toggled bulk mode and
   * dropped the reviewer's tile selection. See `topLayer.ts` for why a module counter rather
   * than context.
   */
  useEffect(() => {
    if (!isOpen) return;
    pushTopLayer();
    return () => popTopLayer();
  }, [isOpen]);

  /**
   * Focus the "What happened?" field on open, and hand focus back to whatever opened the
   * dialog on close. Shaped like `ReviewPageFilter`'s initial-focus effect but targeting the
   * field directly, and depending on the open transition alone so typing never yanks focus
   * back.
   */
  useEffect(() => {
    if (!isOpen) return;
    whatHappenedRef.current?.focus();
    return () => {
      openerRef.current?.focus();
    };
  }, [isOpen]);

  /**
   * Revoke the thumbnail's object URL when the image is replaced, when it is removed, and on
   * unmount — this effect's cleanup covers all three, because it runs whenever `previewUrl`
   * changes as well as on teardown. The lifecycle precedent is `saveBlob` in
   * `review/exportMediaDownload.ts`, which creates and revokes one the same way.
   *
   * A `FileReader` data URL would avoid revocation entirely (`csvImport.ts` records
   * deliberately avoiding object URLs for that reason), but a base64 data URL of a file up to
   * 5 MB is a ~6.7 MB string held in React state for a preview, so the object URL is the
   * right call here.
   */
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  /**
   * Attach one image, validating before anything is attached and without sending a request.
   * A second attachment replaces the first.
   * @param file the candidate file, or null/undefined when the picker was cancelled
   */
  const attachFile = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setAttachError(t("That file is not an image."));
        return;
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        setAttachError(
          t("That image is too large. Please attach one under {{limit}}.", {
            limit: formatBytes(MAX_SCREENSHOT_BYTES),
          }),
        );
        return;
      }
      setAttachError(null);
      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [t],
  );

  /**
   * Paste anywhere while the dialog is open.
   *
   * The listener is on `document`, not on the panel, and that is required rather than
   * convenient: the `paste` event is dispatched at `document.activeElement`, and clicking any
   * non-focusable region of the panel (a context row, the title) moves focus to `<body>`, so
   * the event never enters the panel's subtree and a delegated `onPaste` would never see it.
   * Registered inside the open-keyed effect and removed in its cleanup, so it cannot outlive
   * the dialog.
   *
   * **UNPROVEN: paste handling is a first in this repo** — there is no `onPaste` or
   * `clipboardData` anywhere else in `src/`, and jsdom 16.7.0 has no `ClipboardEvent`, so the
   * unit test drives this with a hand-built `clipboardData` object. That proves the wiring,
   * not that a real browser hands us the pasted image here. Manual QA item.
   *
   * The same effect swallows drags that miss the drop zone. `preventDefault` on the zone's own
   * `dragover`/`drop` only protects the dashed rectangle, and this overlay is `fixed inset-0`:
   * every other pixel on screen — the panel, the gap between the text areas, the backdrop — is
   * an unguarded drop surface, and the browser's default action for a dropped file is to
   * navigate to `file:///…`, which tears down the SPA and takes the typed report with it. The
   * two document listeners make a miss a no-op instead. They do not interfere with the zone:
   * React delegates from the root container, a descendant of `document`, so the zone's own
   * `onDrop` has already attached the file by the time these run.
   */
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (event: ClipboardEvent) => {
      const pasted = event.clipboardData?.files?.[0];
      if (pasted) attachFile(pasted);
    };
    const swallowDrag = (event: Event) => event.preventDefault();
    document.addEventListener("paste", handlePaste);
    document.addEventListener("dragover", swallowDrag);
    document.addEventListener("drop", swallowDrag);
    return () => {
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("dragover", swallowDrag);
      document.removeEventListener("drop", swallowDrag);
    };
  }, [isOpen, attachFile]);

  // Backdrop dismissal, tracked across the whole press rather than on the click alone — the
  // `PointToSpeciesModal` pattern. Here it is not a nicety: a plain backdrop `onClick` fires
  // when a drag that began inside the panel ends on the overlay, which is exactly what
  // dragging an image towards the drop zone and missing looks like.
  const pressStartedOnOverlay = useRef(false);
  const pressEndedOnOverlay = useRef(false);

  const canSend = whatHappened.trim() !== "" && !sending;

  const messageFor = (result: FeedbackResult): string => {
    if (result.outcome === "rateLimited") {
      return t(
        "You have sent several reports in the last hour. Please try again later.",
      );
    }
    if (result.outcome === "signedOut") {
      return t(
        "You are signed out, so this cannot be sent. Please sign in and try again.",
      );
    }
    return t("Could not send your report. Please try again.");
  };

  const handleSend = async () => {
    if (!canSend || !context) return;
    setSending(true);
    setSendError(null);
    const result = await sendFeedbackReport({
      whatHappened: whatHappened.trim(),
      expected: expected.trim(),
      source,
      page: context.page,
      browser: context.browser,
      userAgent: context.userAgent,
      appEnv: context.environment.value,
      apiHost: context.apiHost,
      clientTime: context.clientTime,
      failedRequest,
      aiSessionId,
      screenshot,
    });
    setSending(false);
    if (result.outcome === "sent") {
      // So a later report does not re-attach a failure already told to us.
      clearFailedRequest();
      onSent();
      onClose();
      return;
    }
    // Stay open with every field intact; the sentence renders beside the retry.
    setSendError(messageFor(result));
  };

  /**
   * Escape and the Tab trap, both scoped to the panel.
   *
   * Escape is deliberately **not** `PointToSpeciesModal`'s capture-phase `window` listener:
   * one more of those would be one more layer unable to see the layers above it. The Tab trap
   * keeps focus inside the panel, so a panel-scoped handler sees the key, and propagation is
   * stopped here so the review surface's own Escape never fires.
   *
   * A panel-scoped handler is **not** sufficient on its own, though, and that is what the top
   * layer is for (AKL-798). `PointToSpeciesModal` and `ObservationEditor` answer Escape from
   * capture-phase `window` listeners; React 18 delegates this `onKeyDown` from the root
   * container, a descendant of `window`, so theirs runs first and their `stopPropagation()`
   * does not suppress a sibling capture-phase listener. Before the guard, one Escape inside
   * this dialog cancelled the review dialog underneath — losing the species and comment a
   * reviewer had staged — and this handler never ran at all. `pushTopLayer` while open makes
   * those two listeners stand aside; this handler is then the one that answers.
   * @param event the keydown on the panel
   */
  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (sending) return;
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const environmentValue = context
    ? context.environment.labelKey
      ? t(context.environment.labelKey)
      : context.environment.hostLabel || t("Unknown")
    : "";

  const contextRows: { label: string; value: string }[] = [
    { label: t("Page"), value: context?.page ?? "" },
    { label: t("User"), value: context?.email || t("Unknown") },
    { label: t("Browser"), value: context?.browser || t("Unknown") },
    { label: t("Environment"), value: environmentValue },
    { label: t("Time (UTC)"), value: context?.clientTimeDisplay ?? "" },
  ];

  return (
    <div
      // z-[1400] is above every layer this dialog can be opened over. The toast that opens it
      // is clickable from inside the full-screen review theater (`fixed inset-0 z-50`), over
      // which PointToSpeciesModal and the observation editor sit at z-[1200] and the
      // keyboard-shortcut help overlay at z-[1300]. `z-50` — enough to clear the header
      // (z-20) and the account menu (z-30) — would sit level with the theater and can render
      // behind it. `modalOverlay` itself carries no z-index by design, so it is appended here.
      className={`${tailwindClassnames.modalOverlay} z-[1400]`}
      onPointerDown={(event) => {
        pressStartedOnOverlay.current = event.target === event.currentTarget;
        pressEndedOnOverlay.current = false;
      }}
      onPointerUp={(event) => {
        pressEndedOnOverlay.current = event.target === event.currentTarget;
      }}
      onPointerCancel={() => {
        // The gesture became something else, so it was never a backdrop press. Cleared here
        // because no pointerup follows a cancel, and a stale `true` would make the NEXT click
        // dismiss the dialog.
        pressStartedOnOverlay.current = false;
        pressEndedOnOverlay.current = false;
      }}
      onClick={() => {
        const backdropPress =
          pressStartedOnOverlay.current && pressEndedOnOverlay.current;
        pressStartedOnOverlay.current = false;
        pressEndedOnOverlay.current = false;
        if (!backdropPress || sending) return;
        onClose();
      }}
      data-testid="report-problem-overlay"
    >
      <div
        ref={panelRef}
        className={`${tailwindClassnames.modalPanel} max-w-2xl`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-problem-title"
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        onClick={(event) => {
          // The Tab trap constrains Tab, not clicks: a click on a non-focusable child moves
          // focus to <body>. Pull it back to the panel so keyboard focus stays somewhere
          // sensible (and so a subsequent Escape still reaches this handler).
          const target = event.target as HTMLElement;
          if (!target.closest(FOCUSABLE_SELECTOR)) panelRef.current?.focus();
        }}
        data-testid="report-problem-modal"
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2
            id="report-problem-title"
            className={tailwindClassnames.modalTitle}
          >
            {t("Report a problem")}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
            disabled={sending}
            data-testid="report-problem-close"
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          <div>
            <label
              className={tailwindClassnames.textInputLabel}
              htmlFor="report-what-happened"
            >
              {t("What happened?")}
            </label>
            <textarea
              id="report-what-happened"
              ref={whatHappenedRef}
              rows={3}
              maxLength={MAX_REPORT_FIELD_LENGTH}
              className={TEXTAREA_CLASS}
              value={whatHappened}
              onChange={(event) => setWhatHappened(event.target.value)}
              placeholder={t(
                "e.g. I clicked Save & Next on an image and my labels disappeared",
              )}
              disabled={sending}
              data-testid="report-what-happened"
            />
          </div>

          <div>
            <label
              className={tailwindClassnames.textInputLabel}
              htmlFor="report-expected"
            >
              {t("What did you expect to happen? (optional)")}
            </label>
            <textarea
              id="report-expected"
              rows={2}
              maxLength={MAX_REPORT_FIELD_LENGTH}
              className={TEXTAREA_CLASS}
              value={expected}
              onChange={(event) => setExpected(event.target.value)}
              placeholder={t("e.g. The next image loads with my labels saved")}
              disabled={sending}
              data-testid="report-expected"
            />
          </div>

          <div>
            <p className={tailwindClassnames.textInputLabel}>
              {t("Attach a screenshot (optional)")}
            </p>
            {/* UNPROVEN: an HTML5 drop target is a first in this repo. There is no `onDrop`,
                `onDragOver`, `onDragEnter`, `onDragLeave` or `dataTransfer` anywhere else in
                `src/` (`react-draggable` IS a dependency and IS used, but for a draggable
                popup, not a drop target — no new library is needed here). jsdom 16.7.0 has no
                `DragEvent` or `DataTransfer`, so the unit test drives `onDrop` with a
                hand-built `dataTransfer` object: that proves the wiring, not real browser drag
                behaviour. Manual QA item. */}
            <button
              type="button"
              className={`${DROP_ZONE_BASE} ${
                isDragActive ? DROP_ZONE_ACTIVE : DROP_ZONE_IDLE
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                dragDepth.current += 1;
                setIsDragActive(true);
              }}
              onDragOver={(event) => {
                // Required: without preventDefault on dragOver the browser navigates to the
                // dropped file and the dialog is gone.
                event.preventDefault();
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                dragDepth.current = Math.max(0, dragDepth.current - 1);
                if (dragDepth.current === 0) setIsDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                dragDepth.current = 0;
                setIsDragActive(false);
                attachFile(event.dataTransfer?.files?.[0]);
              }}
              disabled={sending}
              data-testid="report-drop-zone"
            >
              {t("Drag an image here, paste, or browse")}
            </button>
            {/* Hidden, and tabIndex -1 so it stays out of the Tab trap's cycle; the dashed
                zone above is the real, keyboard-operable control. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              tabIndex={-1}
              onChange={(event) => {
                attachFile(event.target.files?.[0]);
                // Clear the value so re-picking the same file fires change again.
                event.target.value = "";
              }}
              data-testid="report-file-input"
            />

            {screenshot && (
              <div className="mt-2 flex items-center gap-3">
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt=""
                    className="h-16 w-16 flex-none rounded-md border border-off-white-dark object-cover dark:border-storm-blue-border"
                    data-testid="report-screenshot-thumbnail"
                  />
                )}
                <span
                  className={`${tailwindClassnames.helperText} break-all`}
                  data-testid="report-screenshot-name"
                >
                  {screenshot.name}
                </span>
                <button
                  type="button"
                  className={tailwindClassnames.btnGhost}
                  onClick={() => {
                    setScreenshot(null);
                    setPreviewUrl(null);
                    setAttachError(null);
                  }}
                  aria-label={t("Remove screenshot")}
                  disabled={sending}
                  data-testid="report-remove-screenshot"
                >
                  {t("Remove screenshot")}
                </button>
              </div>
            )}

            {attachError && (
              <div
                role="alert"
                className={`${tailwindClassnames.warningLabel} mt-2 inline-block`}
                data-testid="report-attach-error"
              >
                {attachError}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-off-white-dark bg-off-white-dark/30 px-3 py-2.5 dark:border-storm-blue-border dark:bg-storm-blue-light/10">
            <p className={tailwindClassnames.textInputLabel}>
              {t("Included automatically")}
            </p>
            <dl className="space-y-1">
              {contextRows.map((row) => (
                <div key={row.label} className="flex gap-2">
                  <dt
                    className={`${tailwindClassnames.helperText} w-32 flex-none font-medium`}
                  >
                    {row.label}
                  </dt>
                  <dd
                    className={`${tailwindClassnames.helperText} break-all`}
                    data-testid={`report-context-${row.label}`}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {sendError && (
            <div
              role="alert"
              className={tailwindClassnames.warningLabel}
              data-testid="report-send-error"
            >
              {sendError}
            </div>
          )}
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onClose}
            disabled={sending}
            data-testid="report-cancel"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`relative ${tailwindClassnames.btnPrimary}`}
            onClick={handleSend}
            disabled={!canSend}
            data-testid="report-send"
          >
            {/* Keep the label in layout while sending so the button keeps its width; the
                spinner uses currentColor so it reads on the button fill in both themes. */}
            <span className={sending ? "invisible" : ""}>
              {t("Send report")}
            </span>
            {sending && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportProblemModal;
